import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Buildings, Plus, Pencil, TreeStructure } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StoreHierarchyPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ store_name: '', store_code: '', parent_store_id: '', address: '', pincode: '', city: '', is_return_center: false, tat_hours: 24 });

  useEffect(() => { fetchStores(); }, []);

  const fetchStores = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/stores`, { withCredentials: true });
      setStores(data.stores || []);
    } catch { toast.error('Failed to load stores'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.store_name || !form.store_code) { toast.error('Name and code required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/stores/${editing.store_id}`, form, { withCredentials: true });
        toast.success('Store updated');
      } else {
        await axios.post(`${API}/api/seller/stores`, form, { withCredentials: true });
        toast.success('Store created');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchStores();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const parentStores = stores.filter(s => !s.parent_store_id);
  const getChildren = (pid) => stores.filter(s => s.parent_store_id === pid);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="store-hierarchy-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Store Network</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Manage parent-child store hierarchy ({stores.length} stores)</p>
          </div>
          <Button data-testid="add-store-btn" onClick={() => { setEditing(null); setForm({ store_name: '', store_code: '', parent_store_id: '', address: '', pincode: '', city: '', is_return_center: false, tat_hours: 24 }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Store
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : stores.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Buildings size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No stores configured. Add your first store location.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {parentStores.map(store => (
              <div key={store.store_id} className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0f0f0]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#7d64ff]/10 flex items-center justify-center"><Buildings size={20} className="text-[#7d64ff]" /></div>
                    <div>
                      <p className="font-semibold text-[#0A0A0A]">{store.store_name} <span className="text-xs font-mono text-[#9c9db7] ml-1">{store.store_code}</span></p>
                      <p className="text-xs text-[#6b6b8a]">{store.city}{store.pincode ? ` — ${store.pincode}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {store.is_return_center && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Return Center</span>}
                    <span className="text-xs text-[#6b6b8a]">TAT: {store.tat_hours}h</span>
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(store); setForm({ store_name: store.store_name, store_code: store.store_code, parent_store_id: store.parent_store_id || '', address: store.address, pincode: store.pincode, city: store.city, is_return_center: store.is_return_center, tat_hours: store.tat_hours }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                  </div>
                </div>
                {getChildren(store.store_id).length > 0 && (
                  <div className="bg-[#fafafe] px-5 py-3 space-y-2">
                    {getChildren(store.store_id).map(child => (
                      <div key={child.store_id} className="flex items-center justify-between pl-6 py-2 border-l-2 border-[#7d64ff]/30">
                        <div className="flex items-center gap-2">
                          <TreeStructure size={14} className="text-[#7d64ff]" />
                          <span className="text-sm font-medium text-[#3c3c64]">{child.store_name}</span>
                          <span className="text-xs font-mono text-[#9c9db7]">{child.store_code}</span>
                          {child.is_return_center && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">RC</span>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(child); setForm({ store_name: child.store_name, store_code: child.store_code, parent_store_id: child.parent_store_id || '', address: child.address, pincode: child.pincode, city: child.city, is_return_center: child.is_return_center, tat_hours: child.tat_hours }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {stores.filter(s => s.parent_store_id && !stores.some(p => p.store_id === s.parent_store_id)).map(orphan => (
              <div key={orphan.store_id} className="bg-white rounded-xl border border-orange-200 px-5 py-4 flex items-center justify-between">
                <div><p className="font-semibold text-[#0A0A0A]">{orphan.store_name} <span className="text-xs text-orange-500">(orphan)</span></p></div>
                <Button variant="ghost" size="sm" onClick={() => { setEditing(orphan); setForm({ ...orphan }); setDialogOpen(true); }}><Pencil size={14} /></Button>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="store-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Store' : 'Add Store'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Store Name</Label><Input data-testid="store-name" value={form.store_name} onChange={e => setForm(f => ({ ...f, store_name: e.target.value }))} placeholder="Warehouse Mumbai" /></div>
                <div><Label>Store Code</Label><Input data-testid="store-code" value={form.store_code} onChange={e => setForm(f => ({ ...f, store_code: e.target.value }))} placeholder="MUM-01" /></div>
              </div>
              <div><Label>Parent Store (optional)</Label>
                <select value={form.parent_store_id} onChange={e => setForm(f => ({ ...f, parent_store_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="">None (Top-level)</option>
                  {stores.filter(s => !s.parent_store_id).map(s => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
                </select>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></div>
                <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} /></div>
              </div>
              <div><Label>Processing TAT</Label><Input type="number" value={form.tat_hours} onChange={e => setForm(f => ({ ...f, tat_hours: parseInt(e.target.value) || 24 }))} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_return_center} onChange={e => setForm(f => ({ ...f, is_return_center: e.target.checked }))} className="rounded" /> This is a return center</label>
              <Button data-testid="save-store-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Create'} Store</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
